import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { webOrigin } from '../utils/brand';
import { peek } from '../data/routeData';
import { loadPro } from '../data/routeLoaders';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import MakeOfferModal from '../components/MakeOfferModal';
import ProGallery from '../components/pro/ProGallery';
import RequestBookingModal from '../components/pro/RequestBookingModal';
import ClaimStartSheet from '../components/pro/ClaimStartSheet';
import ProCover, { Logo, KindPills, TrustLine, Bio, craftChips } from '../components/pro/ProCover';
import ProCoverEditor from '../components/pro/ProCoverEditor';
import {
  CtaPill, AboutBlock, ServicesBlock, ShopServicesBlock, SnacksBlock, KnownForBlock, PhotoGridBlock, EventsBlock, WeekBlock, FacesBlock, FlakeBlock,
  HappyHourBlock, WhatsOnBlock, HireBlock, CertsBlock, ReviewsBlock, HoursBlock, WhereBlock, AttributesBlock, BrandsBlock, ShareBlock, MenuBlock, DrinksBlock,
  OffersBlock, OfferStrip, AsPostedBlock, splitBarSections, head,
} from '../components/pro/ProBlocks';
import { TABS, MOBILE_TABS, statsFor, coverFromBiz, coverSlice, blocksFor, orderTabs, resolveTab, tabPath, HOT_PINK, MUTED } from '../components/pro/proPageModel';
import { resolveLayout } from '../components/event/coverLayout';
import { isVendorKind, openState } from '../components/pro/vendor/vendorUi';
import { makeTracker } from '../components/pro/vendor/proTrack';
import { ProButton, ProSheet } from '../components/pro/proUi';
import { SurfacePage, SoftSurface, RING_ON } from '../components/surface';
import useImageRatios from '../hooks/useImageRatios';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import { audienceService, proService, vendorService } from '../services';
import useMediaQuery from '../utils/useMediaQuery';
import { actAs } from '../components/chrome/ProPagePicker';
import { getActivePage, isActivePage, onActivePageChange } from '../services/activePage';

/**
 * ProPage — one page, five bodies (bookable · eatery · bar · venue · shop) and the listed
 * variant, on the Portal vocabulary (design_handoff_pro_page/README.md): the cover is the event
 * cover's geometry with the Pro identity in the text column, the video rides photo 1's tile,
 * pill CTAs and tabs, borderless panels on #F5F5F5, a 380px rail at ≥ 1040px. Owners get EDIT
 * COVER. Every money and claim flow is unchanged — only the presentation moved.
 */
export default function ProPage() {
  const { slug, tab: tabSegment } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { showAlert } = useDialog();
  const phone = useMediaQuery('(max-width: 759px)');
  const narrow = useMediaQuery('(max-width: 1039px)');
  const wide = !phone && !narrow;

  const [biz, setBiz] = useState(() => peek(`pro:${slug}`) || null);
  const [followBusy, setFollowBusy] = useState(false);
  const [loading, setLoading] = useState(() => !peek(`pro:${slug}`));
  const [notFound, setNotFound] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [preselectedService, setPreselectedService] = useState(null);
  const [offerPrefill, setOfferPrefill] = useState(null);
  const [showRequest, setShowRequest] = useState(false);
  const [requestMode, setRequestMode] = useState('book'); // book | private_event | hire
  const [claimResult, setClaimResult] = useState(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const [gallery, setGallery] = useState(null);
  const [showRemoval, setShowRemoval] = useState(false);
  const [persona, setPersona] = useState(getActivePage);
  useEffect(() => onActivePageChange(setPersona), []);
  const [claimBusyOffer, setClaimBusyOffer] = useState(null);
  const tracker = useRef(null);

  // ── the cover document: photos in order with framing, layout, split, count, card ──
  const [editOpen, setEditOpen] = useState(false);
  const [cover, setCover] = useState(() => coverFromBiz(biz));
  const [coverFor, setCoverFor] = useState(biz);
  const [videoOverride, setVideoOverride] = useState(undefined);

  const [closed, setClosed] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const load = useCallback(async (force = true) => {
    setLoading(!peek(`pro:${slug}`));
    try { setBiz(await loadPro(slug, { force })); setNotFound(false); setClosed(null); }
    catch {
      // A missing slug may be a closed page: its old URL says so, and the owner can bring it back.
      const c = await proService.getClosed(slug);
      setClosed(c.success ? c.data : null);
      setNotFound(true);
    }
    finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { load(false); }, [load]);
  // The cover document follows the loaded page. Re-derived during render (not in an effect) so
  // the first paint already has the photos — no white cover flashing before they arrive.
  if (biz !== coverFor) { setCoverFor(biz); setCover(coverFromBiz(biz)); setVideoOverride(undefined); }

  const ratios = useImageRatios(cover.photos.map((p) => p.src));
  const shapedCover = useMemo(() => ({ ...cover, photos: cover.photos.map((p) => ({ ...p, r: ratios[p.src] || p.r, measured: !!ratios[p.src] })) }), [cover, ratios]);
  // A listed page's seeded photos are unvetted scrapes: the cover stays white until the business claims it.
  const coverPhotos = useMemo(() => (biz?.isListed ? [] : coverSlice(shapedCover)), [shapedCover, biz?.isListed]);
  const layoutKey = resolveLayout(shapedCover.layout, coverPhotos);
  const onFocal = useCallback((id, fx, fy) => setCover((c) => ({ ...c, photos: c.photos.map((p) => (p.id === id ? { ...p, fx, fy } : p)) })), []);

  const kind = biz?.kind || 'bookable';
  const vendor = isVendorKind(kind);
  const listed = !!biz?.isListed;
  // `isMine` is anyone on the page (owner, admin, member): they get the top bar and the dashboard
  // CTA. The cover editor is owner / admin only — the API refuses a member's PUT with a 403.
  const canEdit = !!biz?.isMine && (biz?.viewerRole == null || biz.viewerRole === 'owner' || biz.viewerRole === 'admin');
  const owner = canEdit && !listed;
  const video = listed ? null : (videoOverride === undefined ? biz?.coverVideo : videoOverride);

  // Hidden tabs are absent for everyone (the owner previews what visitors see); ABOUT can
  // never be hidden. The owner's saved order applies to desktop and phone alike. Every tab
  // is its own path — /pro/{slug}/events — and the bare page opens on the owner's default
  // tab. A segment that points at a hidden or unknown tab falls back to that default.
  const hiddenTabs = new Set((biz?.hiddenTabs || []).filter((t) => t !== 'about'));
  const visibleTabs = orderTabs([...((phone ? MOBILE_TABS[kind] : TABS[kind]) || TABS.bookable), ...(biz?.linkedClub ? ['club'] : [])], biz?.tabOrder).filter((t) => !(t === 'faces' && biz?.hideFaces) && !hiddenTabs.has(t));
  const tabs = visibleTabs ? (visibleTabs.length ? visibleTabs : ['about']) : null;
  const legacyTab = params.get('tab');
  const tab = resolveTab(tabs, tabSegment || legacyTab, biz?.defaultTab);
  const search = (() => { const n = new URLSearchParams(params); n.delete('tab'); const q = n.toString(); return q ? `?${q}` : ''; })();
  // Old `?tab=` links are rewritten to the path form once the page knows its tabs.
  useEffect(() => {
    if (!biz || !tabs || !legacyTab) return;
    navigate(`${tabPath(slug, tab, tabs, biz.defaultTab)}${search}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biz?.id, legacyTab]);
  const setTab = (t) => { if (t === 'club' && biz.linkedClub) { navigate(`/clubs/${biz.linkedClub.id}`); return; } if (!tabs) return; navigate(`${tabPath(slug, t, tabs, biz?.defaultTab)}${search}`); tracker.current?.track(t === 'menu' || t === 'drinks' || t === 'services' ? 'menu_open' : 'tab_open', { tab: t }); };

  // Analytics — page view once per load (with open/closed at view), plus the taps below.
  useEffect(() => {
    if (!biz || biz.isMine) return;
    tracker.current = makeTracker(slug);
    const st = openState(biz.hours);
    tracker.current.track('page_view', { source: params.get('src') || (document.referrer.includes('/pros') ? 'directory' : 'direct'), openAtView: st ? st.open : null }, { openAtView: st ? st.open : null });
    if (biz.vendorOffers?.length) biz.vendorOffers.forEach((o) => tracker.current.track('offer_view', {}, { offerId: o.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biz?.id]);
  const track = (type, props, extra) => tracker.current?.track(type, props, extra);
  const outbound = (target) => track('outbound', { target });

  const shareUrl = `${webOrigin()}/pro/${slug}`;
  const photos = biz?.photos || [];

  const startOffer = (prefill = null) => { setOfferPrefill(prefill); setShowOffer(true); };
  const messagePage = async () => {
    if (!user) { navigate('/login', { state: { from: `/pro/${slug}` } }); return; }
    const r = await proService.messagePage(slug);
    if (r.success && r.data?.id) navigate(`/messages?thread=${r.data.id}`);
  };
  const startClaim = async () => {
    track('claim_tap');
    if (!user) { navigate('/login', { state: { from: `/pro/${slug}` } }); return; }
    setClaimBusy(true);
    const r = await proService.startClaim(slug);
    setClaimBusy(false);
    if (r.success) { setClaimResult(r.data); load(); }
    else await showAlert({ title: 'CLAIM', message: r.error || 'Could not start the claim.', type: 'error' });
  };
  const openRequest = (mode) => { setRequestMode(mode); setShowRequest(true); track(mode === 'hire' ? 'hire_enquiry' : mode === 'private_event' ? 'private_event_enquiry' : 'request_tap', { source: 'block' }); };
  // People → PROS's REQUEST A QUOTE lands here with ?offer=1 and opens the right sheet at once:
  // the offer for a bookable page, the booking request for a listed one, the enquiry for a place.
  useEffect(() => {
    if (!biz || params.get('offer') !== '1') return;
    const n = new URLSearchParams(params); n.delete('offer'); setParams(n, { replace: true });
    if (biz.isMine) return;
    if (listed) openRequest('book');
    else if (kind === 'eatery') openRequest('private_event');
    else if (kind === 'bar' || kind === 'venue') openRequest('hire');
    else startOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biz?.id]);
  const claimOffer = async (o) => {
    if (!user) { navigate('/login', { state: { from: `/pro/${slug}/offers` } }); return; }
    setClaimBusyOffer(o.id);
    const r = await vendorService.claimOffer(slug, o.id);
    setClaimBusyOffer(null);
    if (r.success) { track('offer_claim', {}, { offerId: o.id }); load(); }
    else await showAlert({ title: 'CLAIM', message: r.error || 'Could not claim that.', type: 'error' });
  };
  const releaseOffer = async (claim) => { const r = await vendorService.releaseClaim(claim.id); if (r.success) load(); };
  const toggleFollow = async () => {
    if (!user) { navigate(`/login?redirect=${encodeURIComponent(`/pro/${slug}`)}`); return; }
    setFollowBusy(true);
    const r = biz.isFollowing ? await audienceService.unfollowPro(slug) : await audienceService.followPro(slug);
    setFollowBusy(false);
    if (r.success) setBiz((b) => ({ ...b, isFollowing: r.data.isFollowing, followerCount: r.data.followerCount }));
  };
  const openGallery = (i) => { track(phone ? 'gallery_story' : 'gallery_lightbox'); setGallery(i); };
  const goPros = () => navigate('/people?tab=pros');
  const goOwner = () => biz?.ownerHandle && navigate(`/${biz.ownerHandle}`);

  const frame = (children, opts = {}) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile {...opts}>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1280, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap: phone ? 10 : 16, boxSizing: 'border-box' }} data-testid={biz && !loading && !notFound ? (phone ? 'pro-mobile' : 'pro-desktop') : undefined}>
          {children}
        </main>
      </SurfacePage>
    </SoftSurface>
  );

  if (loading) return frame(<div className="py-24"><LoadingSpinner label="LOADING…" /></div>);
  if (closed) {
    const daysLeft = Math.max(0, Math.ceil((new Date(closed.restoreUntil).getTime() - Date.now()) / 86400000));
    const restore = async () => {
      setRestoring(true);
      const r = await proService.restorePage(closed.id);
      setRestoring(false);
      if (r.success) { setClosed(null); navigate(`/pro/${r.data.slug}`, { replace: true }); load(true); }
      else await showAlert({ title: 'COULD NOT BRING IT BACK', message: r.error || 'Try again.', type: 'error' });
    };
    return frame(
      <div className="mx-auto flex w-full flex-col items-center border-2 border-black bg-white text-center" style={{ maxWidth: 640, padding: phone ? 24 : 32, gap: 14 }} data-testid="pro-closed">
        <span className="font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.2em', color: MUTED }}>THIS PAGE CLOSED</span>
        <h1 className="m-0 font-bold uppercase" style={{ fontSize: phone ? 22 : 28, letterSpacing: '0.04em', lineHeight: 1.1 }}>{closed.name}</h1>
        <p className="m-0" style={{ fontSize: 13, lineHeight: 1.6, color: '#374151', maxWidth: 420 }}>
          {closed.canRestore
            ? `You closed it on ${new Date(closed.deletedAt).toLocaleDateString()}. Everything — photos, menu, events, your contact list — is still here for ${daysLeft} more ${daysLeft === 1 ? 'day' : 'days'}. After that it is gone for good.`
            : 'The owner closed it. It is no longer on Portal.'}
        </p>
        <div className="flex flex-wrap justify-center" style={{ gap: 8 }}>
          {closed.canRestore && <ProButton fill onClick={restore} disabled={restoring} data-testid="restore-page">{restoring ? 'RESTORING…' : 'BRING IT BACK'}</ProButton>}
          <ProButton onClick={() => navigate('/pros')}>BACK TO PROS</ProButton>
        </div>
      </div>,
    );
  }
  if (notFound || !biz) return frame(<div className="py-24 text-center" style={{ fontSize: 13, color: MUTED }}>No such Pro page.</div>);

  const liveOffers = (biz.vendorOffers || []).filter((o) => o.phase === 'live' || o.viewerClaim);
  const unclaimedLive = liveOffers.filter((o) => !o.viewerClaim || o.viewerClaim.state !== 'active').length;
  const actingHere = biz.isMine && isActivePage(biz, persona);
  const obH = phone ? 36 : 40;
  const topPill = (on) => ({ height: obH, padding: `0 ${phone ? 14 : 18}px`, border: 'none', borderRadius: 999, background: on ? HOT_PINK : '#fff', color: on ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, textTransform: 'uppercase', transition: 'background-color .2s, color .2s' });

  /* ── CTAs per kind and state ── */
  const ctas = (() => {
    if (biz.isMine) return [<CtaPill key="m" primary phone={phone} onClick={() => navigate(vendor ? '/pro/dashboard' : '/pro/desk')}>{vendor ? 'DASHBOARD →' : 'BOOKING DESK →'}</CtaPill>];
    const claimBtn = biz.viewerStartedClaim
      ? <CtaPill key="c" phone={phone} disabled>CLAIM STARTED — CHECK YOUR INBOX</CtaPill>
      : <CtaPill key="c" phone={phone} onClick={startClaim} disabled={claimBusy}>{claimBusy ? 'ONE MOMENT…' : 'IS THIS YOUR BUSINESS? CLAIM IT'}</CtaPill>;
    if (listed) {
      return [(kind === 'bookable' || kind === 'shop')
        ? <CtaPill key="p" primary phone={phone} onClick={() => openRequest('book')}>REQUEST TO BOOK</CtaPill>
        : <CtaPill key="p" primary phone={phone} onClick={() => openRequest('private_event')}>ASK ABOUT A PRIVATE EVENT</CtaPill>, claimBtn];
    }
    const follow = <CtaPill key="f" phone={phone} onClick={toggleFollow} disabled={followBusy} data-testid="follow-button" aria-pressed={!!biz.isFollowing}>{biz.isFollowing ? 'FOLLOWING ✓' : 'FOLLOW'}{biz.followerCount > 0 ? ` · ${biz.followerCount.toLocaleString()}` : ''}</CtaPill>;
    switch (kind) {
      case 'eatery': {
        const primary = biz.reserveUrl ? <CtaPill key="p" primary phone={phone} href={biz.reserveUrl} onClick={() => outbound('reserve')}>RESERVE ↗</CtaPill>
          : biz.phone ? <CtaPill key="p" primary phone={phone} tel={biz.phone} onClick={() => outbound('call')}>CALL</CtaPill> : null;
        return [primary, <CtaPill key="s" primary={!primary} phone={phone} onClick={() => openRequest('private_event')}>ASK ABOUT A PRIVATE EVENT</CtaPill>, follow].filter(Boolean);
      }
      case 'bar': return [<CtaPill key="p" primary phone={phone} onClick={() => setTab('drinks')}>SEE WHAT’S ON</CtaPill>, <CtaPill key="s" phone={phone} onClick={() => openRequest('hire')}>BOOK THE ROOM</CtaPill>, follow];
      case 'venue': {
        const next = (biz.venueEvents || []).find((e) => new Date(e.dateTime) >= new Date());
        return [next ? <CtaPill key="p" primary phone={phone} onClick={() => navigate(`/events/${next.id}`)}>GET TICKETS</CtaPill> : <CtaPill key="p" primary phone={phone} onClick={() => setTab('events')}>EVENTS</CtaPill>, <CtaPill key="s" phone={phone} onClick={() => openRequest('hire')}>HIRE THIS ROOM</CtaPill>, follow];
      }
      case 'shop': return [<CtaPill key="p" primary phone={phone} onClick={() => startOffer({ message: 'Looking to book a service.' })}>BOOK / GET A QUOTE</CtaPill>, biz.phone ? <CtaPill key="s" phone={phone} tel={biz.phone} onClick={() => outbound('call')}>CALL</CtaPill> : <CtaPill key="s" phone={phone} onClick={messagePage}>MESSAGE</CtaPill>, follow];
      default: return [<CtaPill key="p" primary phone={phone} onClick={() => startOffer()}>SEND AN OFFER</CtaPill>, <CtaPill key="s" phone={phone} onClick={messagePage}>MESSAGE</CtaPill>, follow];
    }
  })();
  const ctaFs = phone ? (ctas.length > 2 || ['eatery', 'shop', 'venue'].includes(kind) || listed ? 9 : 10) : undefined;

  /* ── Body blocks ── */
  const bar = kind === 'bar' ? splitBarSections(biz.menuSections) : null;
  const knownLink = kind === 'bar' ? 'SEE THE DRINKS →' : vendor && kind !== 'shop' && kind !== 'venue' ? (phone ? 'FULL MENU →' : 'SEE THE FULL MENU →') : 'ALL SERVICES →';
  const onKnown = () => {
    if (kind === 'bar') setTab('drinks');
    else if (kind === 'eatery') setTab('menu');
    else if (kind === 'shop') setTab('services');
    else if (phone) setTab('services');
    else document.getElementById('pro-services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const render = {
    known: () => (listed ? null : <KnownForBlock key="known" topThings={biz.topThings} phone={phone} linkLabel={knownLink} onLink={onKnown} onTap={(t) => track('top_thing_tap', { item: t.name })} />),
    strip: () => (phone && !listed ? <OfferStrip key="strip" count={unclaimedLive} onSee={() => setTab('offers')} phone /> : null),
    about: () => <AboutBlock key="about" biz={biz} phone={phone} onOutbound={outbound} />,
    services: () => <ServicesBlock key="services" biz={biz} phone={phone} onBook={(s) => { setPreselectedService(s.id); startOffer(); }} onOffer={() => startOffer()} />,
    shopServices: () => <ShopServicesBlock key="shop" biz={biz} phone={phone} onBook={(it) => startOffer({ amountCents: it.priceCents, message: `${it.bookMode === 'book' ? 'Book' : 'Quote for'}: ${it.name}` })} />,
    events: () => (hiddenTabs.has('events') ? null : <EventsBlock key="events" biz={biz} phone={phone} hideEmpty={kind === 'bookable' && !phone} onEvent={(e) => navigate(`/events/${e.id}`)} />),
    reviews: () => <ReviewsBlock key="reviews" biz={biz} phone={phone} />,
    work: () => (kind === 'bookable' && !phone && photos.length === 0 ? null : <PhotoGridBlock key="work" title={kind === 'bookable' ? 'WORK' : 'PHOTOS'} photos={photos} phone={phone} listed={listed} onOpen={openGallery} />),
    week: () => <WeekBlock key="week" biz={biz} phone={phone} />,
    faces: () => (biz.hideFaces ? null : <FacesBlock key="faces" biz={biz} phone={phone} onFace={(t) => navigate(`/${t.linkedHandle}`)} />),
    flake: () => <FlakeBlock key="flake" biz={biz} phone={phone} />,
    share: () => <ShareBlock key="share" url={shareUrl} listed={listed} phone={phone} />,
    menu: () => <MenuBlock key="menu" biz={biz} phone={phone} onOutbound={outbound} />,
    drinks: () => <DrinksBlock key="drinks" biz={biz} phone={phone} />,
    snacks: () => <SnacksBlock key="snacks" sections={bar?.snacks} phone={phone} />,
    happy: () => <HappyHourBlock key="happy" biz={biz} phone={phone} />,
    whatson: () => <WhatsOnBlock key="whatson" biz={biz} phone={phone} />,
    hire: () => <HireBlock key="hire" biz={biz} phone={phone} onHire={() => openRequest('hire')} />,
    brands: () => <BrandsBlock key="brands" biz={biz} phone={phone} />,
    certs: () => <CertsBlock key="certs" biz={biz} phone={phone} />,
    hours: () => <HoursBlock key="hours" biz={biz} phone={phone} />,
    where: () => <WhereBlock key="where" biz={biz} phone={phone} onOutbound={outbound} />,
    attrs: () => <AttributesBlock key="attrs" biz={biz} phone={phone} />,
    offers: () => <OffersBlock key="offers" biz={biz} offers={liveOffers} phone={phone} onClaim={claimOffer} onRelease={releaseOffer} busyId={claimBusyOffer} canClaimHint={!user ? 'Sign in to claim' : undefined} />,
    asPosted: () => (listed ? <AsPostedBlock key="asposted" phone={phone} /> : null),
  };
  const { main, rail } = blocksFor(kind, tab, { phone });
  const draw = (keys) => keys.map((k) => render[k]?.()).filter(Boolean);
  const mainNodes = draw(main);
  const railNodes = wide ? draw(rail) : [];
  const stacked = !wide ? draw(rail) : [];
  const cols = railNodes.length > 0 ? 'minmax(0,1fr) 380px' : 'minmax(0,1fr)';

  const stats = statsFor(biz, unclaimedLive);

  const modals = (
    <>
      {showRequest && <RequestBookingModal slug={slug} businessName={biz.name} mode={requestMode} onClose={() => setShowRequest(false)} />}
      {claimResult && <ClaimStartSheet businessName={biz.name} result={claimResult} onClose={() => setClaimResult(null)} />}
      {gallery != null && photos.length > 0 && <ProGallery photos={photos} index={gallery} businessName={biz.name} onClose={() => setGallery(null)} onOffer={!listed && !biz.isMine && !vendor ? () => { setGallery(null); startOffer(); } : undefined} />}
      {showRemoval && <RemovalSheet slug={slug} businessName={biz.name} onClose={() => setShowRemoval(false)} />}
      <MakeOfferModal isOpen={showOffer} onClose={() => { setShowOffer(false); setPreselectedService(null); setOfferPrefill(null); }} slug={slug} businessName={biz.name} services={biz.services || []} initialServiceId={preselectedService} initialAmountCents={offerPrefill?.amountCents} initialMessage={offerPrefill?.message} />
    </>
  );

  const topBar = (
    <div className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
      <button type="button" onClick={goPros} className="font-mono hover:bg-black hover:text-white" style={{ ...topPill(false), padding: '0 16px 0 12px' }} data-testid="back-to-pros">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
        PROS
      </button>
      {biz.isMine && (
        <div className="flex flex-wrap items-center" style={{ gap: 8 }} data-testid="owner-bar">
          {actingHere
            ? <span style={topPill(true)} data-testid="acting-as">ACTING AS THIS PAGE</span>
            : <button type="button" onClick={() => actAs({ ...biz, role: biz.viewerRole || 'owner' })} className="font-mono" style={topPill(false)} data-testid="act-as" title="Wear this page: message, post and manage as it">ACT AS THIS PAGE</button>}
          <button type="button" onClick={() => navigate('/pro/dashboard')} className="font-mono hover:bg-[#FF00AA]" style={{ ...topPill(false), background: '#000', color: '#fff', fontWeight: 800, letterSpacing: '0.16em' }} data-testid="manage-page">MANAGE</button>
        </div>
      )}
    </div>
  );

  const coverNode = (
    <ProCover biz={biz} photos={coverPhotos} layout={layoutKey} split={shapedCover.split} video={video} phone={phone} owner={owner} editing={owner && editOpen}
      onToggleEdit={() => setEditOpen((o) => !o)} onFocal={onFocal} onOpenPhoto={openGallery} onOwner={goOwner} onOutbound={outbound} />
  );
  const editor = owner && editOpen && (
    <ProCoverEditor pageId={biz.id} cover={shapedCover} setCover={setCover} video={video} onVideoChange={setVideoOverride} onReload={() => load()} onClose={() => setEditOpen(false)} phone={phone} hasLogo={!!biz.logoUrl} />
  );
  const ctaRow = <div className="flex items-stretch" style={{ gap: 8 }} data-testid="cta-row">{React.Children.map(ctas, (c) => (ctaFs ? React.cloneElement(c, { fontSize: ctaFs }) : c))}</div>;
  const tabRow = tabs && (
    <div role="tablist" className="flex" style={{ flexWrap: phone ? 'nowrap' : 'wrap', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }} data-testid="pro-tabs">
      {tabs.map((t) => (
        <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className="flex-shrink-0 font-mono"
          style={{ height: phone ? 36 : 40, padding: '0 18px', border: '2px solid transparent', borderRadius: 999, background: tab === t ? RING_ON : '#fff', color: tab === t ? '#fff' : '#000', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer', textTransform: 'uppercase', transition: 'background-color .2s, color .2s' }}>
          {t}
        </button>
      ))}
    </div>
  );
  const body = (
    <div className="grid items-start" style={{ gridTemplateColumns: cols, gap: phone ? 10 : 16 }} data-testid={railNodes.length ? 'about-columns' : undefined}>
      <div className="flex min-w-0 flex-col" style={{ gap: phone ? 10 : 16 }} data-testid="about-main">{mainNodes}{stacked}</div>
      {railNodes.length > 0 && <div className="flex min-w-0 flex-col" style={{ gap: 16 }} data-testid="about-rail">{railNodes}</div>}
    </div>
  );
  const removalLink = listed && <button type="button" onClick={() => setShowRemoval(true)} className="self-center font-mono lowercase underline hover:opacity-80" style={{ fontSize: 11, color: MUTED, background: 'none', border: 'none', cursor: 'pointer' }}>not right? ask us to remove it</button>;

  /* ── Phone ── */
  if (phone) {
    const chips = craftChips(biz);
    return frame(
      <>
        {topBar}
        {coverNode}
        <section className="flex flex-col bg-white" style={{ borderRadius: 22, padding: '18px 16px', gap: 12 }} data-testid="identity-panel">
          <div className="flex items-center" style={{ gap: 14 }}>
            <Logo biz={biz} size={72} radius={18} fontSize={30} />
            <span className="flex min-w-0 flex-col" style={{ gap: 8 }}>
              <KindPills biz={biz} small />
              <h1 className="m-0" style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, textTransform: 'none', overflowWrap: 'anywhere' }}>{biz.name}</h1>
            </span>
          </div>
          {chips.length > 0 && <span style={{ ...head(10, '0.12em'), color: MUTED }}>{chips.join(' · ')}</span>}
          <TrustLine biz={biz} size={11} />
          <Bio biz={biz} phone />
          {!listed && !biz.hideOwnerOnCover && biz.ownerHandle && (
            <button type="button" onClick={goOwner} className="flex items-center text-left font-mono" style={{ gap: 10, padding: '6px 14px 6px 6px', borderRadius: 999, background: '#F5F5F5', border: 'none', cursor: 'pointer', color: 'inherit' }} data-testid="managed-by">
              <span className="flex flex-none items-center justify-center overflow-hidden rounded-full" style={{ width: 36, height: 36, background: '#E5E7EB', fontWeight: 700 }}>{biz.ownerAvatarUrl ? <img src={biz.ownerAvatarUrl} alt="" className="block h-full w-full object-cover" /> : (biz.ownerName || '?').charAt(0).toUpperCase()}</span>
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}><span style={{ ...head(8), color: MUTED }}>MANAGED BY</span><span className="truncate" style={{ fontSize: 12, fontWeight: 700 }}>{biz.ownerName || `@${biz.ownerHandle}`}</span></span>
              <span style={{ ...head(9, '0.12em'), color: MUTED }}>PORTAL PAGE →</span>
            </button>
          )}
        </section>
        <div className="grid overflow-hidden" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4, borderRadius: 22 }} data-testid="stats-strip">
          {stats.map(([n, k, c]) => (
            <span key={k} className="flex flex-col items-center bg-white text-center" style={{ gap: 4, padding: '14px 6px' }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: listed ? '#9CA3AF' : (c || '#000') }}>{n}</span>
              <span style={{ ...head(8, '0.14em'), color: MUTED }}>{k}</span>
            </span>
          ))}
        </div>
        {editor}
        {ctaRow}
        {tabRow}
        {body}
        {removalLink}
        {modals}
      </>,
    );
  }

  /* ── Desktop ── */
  return frame(
    <>
      {topBar}
      {coverNode}
      {editor}
      {ctaRow}
      {tabRow}
      {body}
      {removalLink}
      {modals}
    </>,
  );
}

/** "not right? ask us to remove it" — a reason and an optional contact, then an admin looks. */
function RemovalSheet({ slug, businessName, onClose }) {
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const send = async () => { setBusy(true); const r = await proService.requestRemoval(slug, { reason: reason.trim() || null, contactEmail: email.trim() || null }); setBusy(false); if (r.success) setDone(true); };
  return (
    <ProSheet width={420} title="ASK US TO REMOVE IT" onClose={onClose} testId="removal-sheet"
      footer={done ? <ProButton fill size="md" onClick={onClose}>DONE</ProButton> : (<><ProButton size="md" onClick={onClose} disabled={busy}>CANCEL</ProButton><ProButton fill size="md" onClick={send} disabled={busy}>{busy ? 'SENDING…' : 'SEND'}</ProButton></>)}>
      <div className="flex flex-col gap-3" style={{ padding: 18 }}>
        {done ? <p style={{ fontSize: 13, lineHeight: 1.6 }}>Thanks. An admin will look at {businessName} and take it down if it shouldn&apos;t be here.</p> : (
          <>
            <p style={{ fontSize: 13, lineHeight: 1.6, color: MUTED }}>This page was built from public information about {businessName}. Tell us what&apos;s wrong and we&apos;ll review it.</p>
            <textarea rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What's not right?" aria-label="Reason" className="w-full resize-none border-2 border-black px-3 py-2 outline-none" style={{ fontSize: 13 }} />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email (optional)" aria-label="Your email" className="w-full border-2 border-black px-3 outline-none" style={{ fontSize: 12, height: 40 }} />
          </>
        )}
      </div>
    </ProSheet>
  );
}
